/**
 * Canvas State Management with Zustand
 * Manages workflow canvas state, nodes, connections, and interactions
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import {
  CanvasState,
  CanvasNode,
  Connection,
  Position,
  InteractionMode,
  DragState,
  SelectionRect,
  HistoryState,
  Workflow,
  WorkflowNode,
  getNodeColor
} from '../types/workflow';
import type { 
  WorkflowGraph, 
  NodeDefinition, 
  WorkflowNode as UniversalWorkflowNode,
  NodeLibrary
} from '../types/node-definitions';
import { 
  generateNodeId, 
  createNodeInstance,
  setFieldValue
} from '../utils/node-helpers';
import { TypeValidationService } from '../services/TypeValidationService';
import { TypeCompatibilityChecker } from '../constants/TypeCompatibilityMatrix';
import { EnhancedNodeType } from '../types/enhanced-types';
import { workflowPersistenceService } from '../services/WorkflowPersistenceService';

interface CanvasStore extends CanvasState {
  // Universal node system
  nodeLibrary: NodeLibrary | null;
  universalNodes: Record<string, UniversalWorkflowNode>;
  nodeDefinitions: Record<string, NodeDefinition>;
  
  // Interaction state
  interactionMode: InteractionMode;
  dragState: DragState;
  selectionRect: SelectionRect;
  isConnecting: boolean;
  connectionStart: { nodeId: string; socketId: string } | null;
  
  // History for undo/redo
  history: HistoryState[];
  historyIndex: number;
  maxHistorySize: number;
  
  // Clipboard for copy/paste
  clipboard: {
    nodes: CanvasNode[];
    universalNodes: Record<string, UniversalWorkflowNode>;
    connections: Connection[];
  } | null;
  
  // Flag to prevent multiple saveState calls during batch operations
  batchOperation: boolean;
  
  // Universal node actions
  loadNodeLibrary: (library: NodeLibrary) => void;
  updateNodeDefinitions: (definitions: Record<string, NodeDefinition>) => void;
  addUniversalNode: (definitionId: string, position: Position, initialValues?: Record<string, any>) => string;
  updateNodeField: (nodeId: string, fieldId: string, value: any) => void;
  getNodeDefinition: (nodeId: string) => NodeDefinition | null;
  addUniversalConnection: (connectionData: {
    id: string;
    from: { nodeId: string; outputId: string };
    to: { nodeId: string; inputId: string };
  }) => void;
  
  // Legacy actions (kept for compatibility)
  addNode: (classType: string, position: Position, nodeData?: any) => string;
  removeNode: (nodeId: string) => void;
  updateNode: (nodeId: string, updates: Partial<CanvasNode>) => void;
  moveNode: (nodeId: string, position: Position) => void;
  
  addConnection: (sourceNodeId: string, sourceSocketId: string, targetNodeId: string, targetSocketId: string) => string;
  removeConnection: (connectionId: string) => void;
  
  // Node Definitions Management
  setNodeDefinitions: (definitions: Record<string, NodeDefinition>) => void;
  
  selectNode: (nodeId: string, multiSelect?: boolean) => void;
  selectNodes: (nodeIds: string[]) => void;
  clearSelection: () => void;
  selectAll: () => void;
  
  setViewport: (x: number, y: number, scale: number) => void;
  panViewport: (deltaX: number, deltaY: number) => void;
  zoomViewport: (scale: number, center?: Position) => void;
  
  setInteractionMode: (mode: InteractionMode) => void;
  startDrag: (dragType: DragState['dragType'], startPos: Position, items?: Set<string>) => void;
  updateDrag: (currentPos: Position) => void;
  endDrag: () => void;
  
  startSelection: (startPos: Position) => void;
  updateSelection: (endPos: Position) => void;
  endSelection: () => void;
  
  startConnection: (nodeId: string, socketId: string) => void;
  updateConnection: (position: Position) => void;
  endConnection: (nodeId?: string, socketId?: string) => void;
  
  // Workflow operations (enhanced for universal nodes)
  loadWorkflow: (workflow: Workflow) => void;
  
  // Enhanced persistence operations
  initializePersistence: () => Promise<void>;
  hasUnsavedChanges: () => boolean;
  saveCurrentWorkflow: (name?: string) => Promise<{ success: boolean; id?: string; error?: string }>;
  getRecentWorkflows: () => Promise<any[]>;
  enableAutoSave: (enabled: boolean) => void;
  loadUniversalWorkflow: (workflow: WorkflowGraph) => void;
  exportWorkflow: () => Workflow;
  exportUniversalWorkflow: () => WorkflowGraph;
  clearWorkflow: () => void;
  
  // History operations
  saveState: (description: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  
  // Copy/Paste operations
  copySelectedNodes: () => void;
  pasteNodes: (position?: Position) => void;
  canPaste: () => boolean;
  
  // Node display mode operations
  toggleNodeCollapse: (nodeId: string) => void;
  changeNodeDisplayMode: (nodeId: string, mode: 'normal' | 'expanded' | 'minimized' | 'maximized') => void;
  
  // Utility functions
  getSelectedNodes: () => CanvasNode[];
  getNodeConnections: (nodeId: string) => Connection[];
  findConnectionsToSocket: (nodeId: string, socketId: string) => Connection[];
  isValidConnection: (sourceNodeId: string, sourceSocketId: string, targetNodeId: string, targetSocketId: string) => boolean;
  areTypesCompatible: (sourceType: string, targetType: string) => boolean;
  wouldCreateCycle: (sourceNodeId: string, targetNodeId: string) => boolean;
}

// Create default node
function createDefaultNode(
  id: string,
  classType: string,
  position: Position,
  nodeData: any = {}
): CanvasNode {
  const { color, bgcolor } = getNodeColor(nodeData.category || 'default');
  
  return {
    id,
    position,
    size: { width: 200, height: 120 },
    classType,
    title: nodeData.display_name || classType,
    inputs: [],
    outputs: [],
    selected: false,
    dragging: false,
    color,
    bgcolor,
    data: nodeData
  };
}

// Create default viewport
const defaultViewport = {
  x: 0,
  y: 0,
  scale: 1
};

// Create default grid
const defaultGrid = {
  size: 20,
  visible: true,
  snap: true
};

// Create default drag state
const defaultDragState: DragState = {
  isDragging: false,
  dragType: 'node',
  startPosition: { x: 0, y: 0 },
  currentPosition: { x: 0, y: 0 },
  draggedItems: new Set()
};

// Create default selection rect
const defaultSelectionRect: SelectionRect = {
  start: { x: 0, y: 0 },
  end: { x: 0, y: 0 },
  active: false
};

export const useCanvasStore = create<CanvasStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      nodes: new Map(),
      connections: new Map(),
      selection: new Set(),
      
      // Universal node system state
      nodeLibrary: null,
      universalNodes: {},
      nodeDefinitions: {},
      viewport: defaultViewport,
      grid: defaultGrid,
      
      interactionMode: InteractionMode.SELECT,
      dragState: defaultDragState,
      selectionRect: defaultSelectionRect,
      isConnecting: false,
      connectionStart: null,
      
      history: [],
      historyIndex: -1,
      maxHistorySize: 50,
      
      clipboard: null,
      batchOperation: false,
      
      // Node operations
      addNode: (classType: string, position: Position, nodeData: any = {}) => {
        const id = uuidv4();
        const node = createDefaultNode(id, classType, position, nodeData);
        
        set((state) => {
          const newNodes = new Map(state.nodes);
          newNodes.set(id, node);
          
          return {
            nodes: newNodes,
            selection: new Set([id])
          };
        });
        
        if (!get().batchOperation) {
          get().saveState(`Add ${classType} node`);
        }
        return id;
      },
      
      removeNode: (nodeId: string) => {
        set((state) => {
          const newNodes = new Map(state.nodes);
          const newUniversalNodes = { ...state.universalNodes };
          const newConnections = new Map(state.connections);
          const newSelection = new Set(state.selection);
          
          // Remove node from both legacy and universal collections
          newNodes.delete(nodeId);
          delete newUniversalNodes[nodeId];
          newSelection.delete(nodeId);
          
          // Remove all connections to/from this node
          for (const [connectionId, connection] of newConnections) {
            if (connection.sourceNodeId === nodeId || connection.targetNodeId === nodeId) {
              newConnections.delete(connectionId);
            }
          }
          
          return {
            nodes: newNodes,
            universalNodes: newUniversalNodes,
            connections: newConnections,
            selection: newSelection
          };
        });
        
        get().saveState(`Remove node`);
      },
      
      updateNode: (nodeId: string, updates: Partial<CanvasNode>) => {
        set((state) => {
          const newNodes = new Map(state.nodes);
          const existingNode = newNodes.get(nodeId);
          
          if (existingNode) {
            newNodes.set(nodeId, { ...existingNode, ...updates });
          }
          
          return { nodes: newNodes };
        });
      },
      
      moveNode: (nodeId: string, position: Position) => {
        const { nodes, universalNodes } = get();
        
        // Check if it's a legacy node
        if (nodes.has(nodeId)) {
          get().updateNode(nodeId, { position });
        } 
        // Check if it's a universal node
        else if (universalNodes[nodeId]) {
          get().updateNodeField(nodeId, 'position', position);
        }
      },
      
      // Connection operations
      addConnection: (sourceNodeId: string, sourceSocketId: string, targetNodeId: string, targetSocketId: string) => {
        console.log('Attempting to add connection:', {
          sourceNodeId,
          sourceSocketId,
          targetNodeId,
          targetSocketId
        });
        
        const isValid = get().isValidConnection(sourceNodeId, sourceSocketId, targetNodeId, targetSocketId);
        
        if (!isValid) {
          console.warn('Connection rejected by validation');
          return '';
        }
        
        const connectionId = uuidv4();
        const connection: Connection = {
          id: connectionId,
          sourceNodeId,
          sourceSocketId,
          targetNodeId,
          targetSocketId,
          points: [] // Will be calculated by the canvas renderer
        };
        
        set((state) => {
          const newConnections = new Map(state.connections);
          newConnections.set(connectionId, connection);
          return { connections: newConnections };
        });
        
        get().saveState(`Add connection`);
        return connectionId;
      },
      
      removeConnection: (connectionId: string) => {
        set((state) => {
          const newConnections = new Map(state.connections);
          newConnections.delete(connectionId);
          return { connections: newConnections };
        });
        
        get().saveState(`Remove connection`);
      },
      
      // Node Definitions Management
      setNodeDefinitions: (definitions: Record<string, NodeDefinition>) => {
        set({ nodeDefinitions: definitions });
      },
      
      // Selection operations
      selectNode: (nodeId: string, multiSelect = false) => {
        set((state) => {
          const newSelection = multiSelect ? new Set(state.selection) : new Set<string>();
          
          if (newSelection.has(nodeId)) {
            newSelection.delete(nodeId);
          } else {
            newSelection.add(nodeId);
          }
          
          // Update node selected state
          const newNodes = new Map(state.nodes);
          for (const [id, node] of newNodes) {
            newNodes.set(id, { ...node, selected: newSelection.has(id) });
          }
          
          return {
            nodes: newNodes,
            selection: newSelection
          };
        });
      },
      
      selectNodes: (nodeIds: string[]) => {
        set((state) => {
          const newSelection = new Set(nodeIds);
          const newNodes = new Map(state.nodes);
          
          for (const [id, node] of newNodes) {
            newNodes.set(id, { ...node, selected: newSelection.has(id) });
          }
          
          return {
            nodes: newNodes,
            selection: newSelection
          };
        });
      },
      
      clearSelection: () => {
        get().selectNodes([]);
      },
      
      selectAll: () => {
        const { nodes, universalNodes } = get();
        const nodeIds = [
          ...Array.from(nodes.keys()),
          ...Object.keys(universalNodes)
        ];
        get().selectNodes(nodeIds);
      },
      
      // Viewport operations
      setViewport: (x: number, y: number, scale: number) => {
        set({ viewport: { x, y, scale } });
      },
      
      panViewport: (deltaX: number, deltaY: number) => {
        set((state) => ({
          viewport: {
            ...state.viewport,
            x: state.viewport.x + deltaX,
            y: state.viewport.y + deltaY
          }
        }));
      },
      
      zoomViewport: (scale: number, center?: Position) => {
        set((state) => {
          let newViewport = { ...state.viewport, scale };
          
          if (center) {
            // Zoom to specific point
            const scaleRatio = scale / state.viewport.scale;
            newViewport.x = center.x - (center.x - state.viewport.x) * scaleRatio;
            newViewport.y = center.y - (center.y - state.viewport.y) * scaleRatio;
          }
          
          return { viewport: newViewport };
        });
      },
      
      // Interaction operations
      setInteractionMode: (mode: InteractionMode) => {
        set({ interactionMode: mode });
      },
      
      startDrag: (dragType: DragState['dragType'], startPos: Position, items = new Set<string>()) => {
        set({
          dragState: {
            isDragging: true,
            dragType,
            startPosition: startPos,
            currentPosition: startPos,
            draggedItems: items
          }
        });
      },
      
      updateDrag: (currentPos: Position) => {
        set((state) => ({
          dragState: {
            ...state.dragState,
            currentPosition: currentPos
          }
        }));
      },
      
      endDrag: () => {
        set({
          dragState: {
            ...get().dragState,
            isDragging: false,
            draggedItems: new Set()
          }
        });
      },
      
      // Selection rectangle operations
      startSelection: (startPos: Position) => {
        set({
          selectionRect: {
            start: startPos,
            end: startPos,
            active: true
          }
        });
      },
      
      updateSelection: (endPos: Position) => {
        set((state) => ({
          selectionRect: {
            ...state.selectionRect,
            end: endPos
          }
        }));
      },
      
      endSelection: () => {
        const { selectionRect, nodes, universalNodes } = get();
        
        if (selectionRect.active) {
          // Find nodes within selection rectangle
          const selectedNodeIds: string[] = [];
          const minX = Math.min(selectionRect.start.x, selectionRect.end.x);
          const maxX = Math.max(selectionRect.start.x, selectionRect.end.x);
          const minY = Math.min(selectionRect.start.y, selectionRect.end.y);
          const maxY = Math.max(selectionRect.start.y, selectionRect.end.y);
          
          // Check legacy nodes
          for (const [nodeId, node] of nodes) {
            if (
              node.position.x >= minX &&
              node.position.x + node.size.width <= maxX &&
              node.position.y >= minY &&
              node.position.y + node.size.height <= maxY
            ) {
              selectedNodeIds.push(nodeId);
            }
          }
          
          // Check universal nodes
          for (const [nodeId, node] of Object.entries(universalNodes)) {
            const position = node.values.position || { x: 0, y: 0 };
            const nodeWidth = 200; // Standard node width
            const nodeHeight = 100; // Estimated node height
            
            if (
              position.x >= minX &&
              position.x + nodeWidth <= maxX &&
              position.y >= minY &&
              position.y + nodeHeight <= maxY
            ) {
              selectedNodeIds.push(nodeId);
            }
          }
          
          get().selectNodes(selectedNodeIds);
        }
        
        set({
          selectionRect: {
            ...selectionRect,
            active: false
          }
        });
      },
      
      // Connection operations
      startConnection: (nodeId: string, socketId: string) => {
        set({
          isConnecting: true,
          connectionStart: { nodeId, socketId }
        });
      },
      
      updateConnection: (position: Position) => {
        // Connection preview will be handled by the canvas renderer
      },
      
      endConnection: (nodeId?: string, socketId?: string) => {
        const { connectionStart } = get();
        
        if (connectionStart && nodeId && socketId) {
          get().addConnection(
            connectionStart.nodeId,
            connectionStart.socketId,
            nodeId,
            socketId
          );
        }
        
        set({
          isConnecting: false,
          connectionStart: null
        });
      },
      
      // Workflow operations
      loadWorkflow: (workflow: Workflow) => {
        // Clear existing workflow state first to prevent session contamination
        get().clearWorkflow();
        
        const newNodes = new Map<string, CanvasNode>();
        const newConnections = new Map<string, Connection>();
        
        // Create nodes
        let nodeIndex = 0;
        for (const [nodeId, workflowNode] of Object.entries(workflow)) {
          const position = {
            x: 100 + (nodeIndex % 4) * 250,
            y: 100 + Math.floor(nodeIndex / 4) * 150
          };
          
          const canvasNode = createDefaultNode(
            nodeId,
            workflowNode.class_type,
            position,
            { 
              ...workflowNode._meta,
              inputs: workflowNode.inputs
            }
          );
          
          newNodes.set(nodeId, canvasNode);
          nodeIndex++;
        }
        
        // Create connections from workflow links
        for (const [targetNodeId, workflowNode] of Object.entries(workflow)) {
          for (const [inputName, inputValue] of Object.entries(workflowNode.inputs)) {
            if (Array.isArray(inputValue) && inputValue.length === 2) {
              const [sourceNodeId, outputIndex] = inputValue;
              get().addConnection(
                sourceNodeId,
                `output_${outputIndex}`,
                targetNodeId,
                `input_${inputName}`
              );
            }
          }
        }
        
        set({
          nodes: newNodes,
          connections: newConnections,
          selection: new Set()
        });
        
        get().saveState('Load workflow');
      },
      
      exportWorkflow: (): Workflow => {
        const { nodes, connections } = get();
        const workflow: Workflow = {};
        
        for (const [nodeId, node] of nodes) {
          const workflowNode: WorkflowNode = {
            inputs: {},
            class_type: node.classType,
            _meta: {
              title: node.title,
              color: node.color,
              bgcolor: node.bgcolor
            }
          };
          
          // Add connections as inputs
          for (const connection of connections.values()) {
            if (connection.targetNodeId === nodeId) {
              const inputName = connection.targetSocketId.replace('input_', '');
              const outputIndex = parseInt(connection.sourceSocketId.replace('output_', ''));
              workflowNode.inputs[inputName] = [connection.sourceNodeId, outputIndex];
            }
          }
          
          workflow[nodeId] = workflowNode;
        }
        
        return workflow;
      },
      
      clearWorkflow: () => {
        console.debug('Canvas Store: Clearing workflow state');
        
        set({
          // Core workflow data
          nodes: new Map(),
          connections: new Map(),
          selection: new Set(),
          universalNodes: {},
          
          // Interaction state - reset to defaults
          interactionMode: InteractionMode.Select,
          dragState: {
            isDragging: false,
            dragType: 'node',
            startPosition: { x: 0, y: 0 },
            currentPosition: { x: 0, y: 0 },
            draggedItems: new Set()
          },
          selectionRect: {
            start: { x: 0, y: 0 },
            end: { x: 0, y: 0 },
            active: false
          },
          
          // Connection state
          isConnecting: false,
          connectionStart: null,
          
          // Clear clipboard to prevent stale references
          clipboard: {
            nodes: [],
            universalNodes: {},
            connections: []
          }
          
          // Note: We don't clear viewport, history, or nodeLibrary 
          // as these should persist across workflow changes
        });
        
        get().saveState('Clear workflow');
      },
      
      // Universal node system actions
      loadNodeLibrary: (library: NodeLibrary) => {
        
        set((state) => {
          const newState = {
            nodeLibrary: library,
            nodeDefinitions: {
              ...state.nodeDefinitions,
              ...library.definitions
            }
          };
          
          return newState;
        });
      },

      // Add connection from universal workflow (Rete.js compatible)
      addUniversalConnection: (connectionData: {
        id: string;
        from: { nodeId: string; outputId: string };
        to: { nodeId: string; inputId: string };
      }) => {
        set((state) => ({
          connections: new Map(state.connections).set(connectionData.id, {
            id: connectionData.id,
            sourceNodeId: connectionData.from.nodeId,
            sourceSocketId: connectionData.from.outputId,
            targetNodeId: connectionData.to.nodeId,
            targetSocketId: connectionData.to.inputId,
            points: []
          })
        }));
        
        get().saveState('Add connection');
      },
      
      updateNodeDefinitions: (definitions: Record<string, NodeDefinition>) => {
        set((state) => ({
          nodeDefinitions: {
            ...state.nodeDefinitions,
            ...definitions
          }
        }));
      },
      
      addUniversalNode: (definitionId: string, position: Position, initialValues: Record<string, any> = {}) => {
        const { nodeDefinitions, universalNodes } = get();
        const definition = nodeDefinitions[definitionId];
        
        
        if (!definition) {
          console.error(`❌ Node definition not found: ${definitionId}`);
          return '';
        }
        
        const nodeId = generateNodeId(Object.keys(universalNodes));
        const node = createNodeInstance(definitionId, position, initialValues);
        
        
        set({
          universalNodes: {
            ...universalNodes,
            [nodeId]: node
          }
        });
        
        if (!get().batchOperation) {
          get().saveState(`Add ${definition.meta.title}`);
        }
        return nodeId;
      },
      
      updateNodeField: (nodeId: string, fieldId: string, value: any) => {
        const { universalNodes } = get();
        const node = universalNodes[nodeId];
        
        if (!node) return;
        
        const updatedNode = setFieldValue(node, fieldId, value);
        
        set({
          universalNodes: {
            ...universalNodes,
            [nodeId]: updatedNode
          }
        });
      },
      
      getNodeDefinition: (nodeId: string): NodeDefinition | null => {
        const { universalNodes, nodeDefinitions } = get();
        const node = universalNodes[nodeId];
        
        if (!node) return null;
        
        return nodeDefinitions[node.definitionId] || null;
      },
      
      loadUniversalWorkflow: (workflow: WorkflowGraph) => {
        console.log('📥 Canvas Store: Loading universal workflow', { 
          nodeCount: Object.keys(workflow.nodes || {}).length,
          connectionCount: (workflow.connections || []).length,
          workflow 
        });
        
        // Clear existing workflow state first to prevent session contamination
        get().clearWorkflow();
        
        set({
          universalNodes: workflow.nodes,
          // Convert universal connections to legacy format for now
          connections: new Map(
            workflow.connections.map(conn => [
              conn.id,
              {
                id: conn.id,
                sourceNodeId: conn.from.nodeId,
                sourceSocketId: conn.from.outputId,
                targetNodeId: conn.to.nodeId,
                targetSocketId: conn.to.inputId,
                points: [] // Add required points property
              }
            ])
          ),
          selection: new Set()
        });
        
        get().saveState('Load universal workflow');
        
        // Start new session with loaded workflow
        import('../services/WorkflowPersistenceService').then(({ workflowPersistenceService }) => {
          workflowPersistenceService.startSession(workflow);
        });
      },
      
      exportUniversalWorkflow: (): WorkflowGraph => {
        const { universalNodes, connections } = get();
        
        return {
          meta: {
            name: 'Exported Workflow',
            version: '1.0.0',
            created: new Date().toISOString(),
            description: 'Workflow exported from SengiLabs Editor'
          },
          nodes: universalNodes,
          connections: Array.from(connections.values()).map(conn => ({
            id: conn.id,
            from: {
              nodeId: conn.sourceNodeId,
              outputId: conn.sourceSocketId
            },
            to: {
              nodeId: conn.targetNodeId,
              inputId: conn.targetSocketId
            }
          }))
        };
      },
      
      // History operations
      saveState: (description: string) => {
        const { nodes, connections, universalNodes, history, historyIndex, maxHistorySize } = get();
        
        const historyState: HistoryState = {
          nodes: new Map(nodes),
          connections: new Map(connections),
          universalNodes: JSON.parse(JSON.stringify(universalNodes)), // Deep copy
          timestamp: Date.now(),
          description
        };
        
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(historyState);
        
        // Limit history size
        if (newHistory.length > maxHistorySize) {
          newHistory.shift();
        }
        
        set({
          history: newHistory,
          historyIndex: newHistory.length - 1
        });
      },
      
      undo: () => {
        const { history, historyIndex } = get();
        
        if (historyIndex > 0) {
          const prevState = history[historyIndex - 1];
          set({
            nodes: new Map(prevState.nodes),
            connections: new Map(prevState.connections),
            universalNodes: JSON.parse(JSON.stringify(prevState.universalNodes)), // Deep copy
            historyIndex: historyIndex - 1,
            selection: new Set()
          });
        }
      },
      
      redo: () => {
        const { history, historyIndex } = get();
        
        if (historyIndex < history.length - 1) {
          const nextState = history[historyIndex + 1];
          set({
            nodes: new Map(nextState.nodes),
            connections: new Map(nextState.connections),
            universalNodes: JSON.parse(JSON.stringify(nextState.universalNodes)), // Deep copy
            historyIndex: historyIndex + 1,
            selection: new Set()
          });
        }
      },
      
      canUndo: () => get().historyIndex > 0,
      canRedo: () => get().historyIndex < get().history.length - 1,
      
      // Copy/Paste operations
      copySelectedNodes: () => {
        const { selection, nodes, universalNodes, connections } = get();
        
        if (selection.size === 0) {
          return;
        }
        
        // Copy selected nodes (both legacy and universal)
        const copiedNodes: CanvasNode[] = [];
        const copiedUniversalNodes: Record<string, UniversalWorkflowNode> = {};
        const copiedConnections: Connection[] = [];
        
        // Copy legacy nodes
        selection.forEach(nodeId => {
          const node = nodes.get(nodeId);
          if (node) {
            copiedNodes.push({ ...node });
          }
        });
        
        // Copy universal nodes
        selection.forEach(nodeId => {
          const universalNode = universalNodes[nodeId];
          if (universalNode) {
            copiedUniversalNodes[nodeId] = {
              ...universalNode,
              values: { ...universalNode.values }
            };
          }
        });
        
        // Copy connections between selected nodes
        connections.forEach(connection => {
          if (selection.has(connection.sourceNodeId) && selection.has(connection.targetNodeId)) {
            copiedConnections.push({ ...connection });
          }
        });
        
        set({
          clipboard: {
            nodes: copiedNodes,
            universalNodes: copiedUniversalNodes,
            connections: copiedConnections
          }
        });
        
      },
      
      pasteNodes: (pastePosition?: Position) => {
        const { clipboard, addNode, addUniversalNode, addConnection, viewport } = get();
        
        if (!clipboard) {
          return;
        }
        
        // Start batch operation to prevent multiple saveState calls
        set({ batchOperation: true });
        
        // Calculate paste position (mouse position or viewport center)
        const centerPosition = pastePosition || {
          x: -viewport.x / viewport.scale + 400,
          y: -viewport.y / viewport.scale + 300
        };
        
        // Calculate bounds of copied nodes to center them around paste position
        let minX = Infinity, minY = Infinity;
        const allCopiedNodes = [
          ...clipboard.nodes,
          ...Object.values(clipboard.universalNodes).map(n => ({ position: n.values.position || { x: 0, y: 0 } }))
        ];
        
        allCopiedNodes.forEach(node => {
          const pos = node.position;
          if (pos) {
            minX = Math.min(minX, pos.x);
            minY = Math.min(minY, pos.y);
          }
        });
        
        const offset = {
          x: centerPosition.x - (minX === Infinity ? 0 : minX),
          y: centerPosition.y - (minY === Infinity ? 0 : minY)
        };
        
        const nodeIdMapping: Record<string, string> = {};
        const newSelection = new Set<string>();
        
        // Paste legacy nodes
        clipboard.nodes.forEach(node => {
          const newId = uuidv4();
          nodeIdMapping[node.id] = newId;
          
          const newPosition = {
            x: node.position.x + offset.x + 20, // Small offset to avoid overlap
            y: node.position.y + offset.y + 20
          };
          
          addNode(node.classType, newPosition, {
            ...node.data,
            inputs: { ...node.data.inputs }
          });
          newSelection.add(newId);
        });
        
        // Paste universal nodes
        Object.entries(clipboard.universalNodes).forEach(([oldId, node]) => {
          const newId = generateNodeId();
          nodeIdMapping[oldId] = newId;
          
          const originalPos = node.values.position || { x: 0, y: 0 };
          const newPosition = {
            x: originalPos.x + offset.x + 20,
            y: originalPos.y + offset.y + 20
          };
          
          addUniversalNode(node.definitionId, newPosition, {
            ...node.values,
            position: newPosition
          });
          newSelection.add(newId);
        });
        
        // Paste connections between pasted nodes
        clipboard.connections.forEach(connection => {
          const newSourceId = nodeIdMapping[connection.sourceNodeId];
          const newTargetId = nodeIdMapping[connection.targetNodeId];
          
          if (newSourceId && newTargetId) {
            addConnection(
              newSourceId,
              connection.sourceSocketId,
              newTargetId,
              connection.targetSocketId
            );
          }
        });
        
        // End batch operation and select the pasted nodes
        set({ 
          selection: newSelection,
          batchOperation: false
        });
        
        // Save state after batch operation is complete
        get().saveState(`Paste ${newSelection.size} nodes`);
        
      },
      
      canPaste: () => {
        const { clipboard } = get();
        return clipboard !== null && (
          clipboard.nodes.length > 0 || 
          Object.keys(clipboard.universalNodes).length > 0
        );
      },
      
      // Node display mode operations
      toggleNodeCollapse: (nodeId: string) => {
        const node = get().nodes.get(nodeId);
        if (node) {
          get().updateNode(nodeId, { collapsed: !node.collapsed });
          get().saveState(`Toggle node collapse`);
        }
      },
      
      changeNodeDisplayMode: (nodeId: string, mode: 'normal' | 'expanded' | 'minimized' | 'maximized') => {
        const node = get().nodes.get(nodeId);
        if (node) {
          get().updateNode(nodeId, { displayMode: mode });
          get().saveState(`Change node display mode to ${mode}`);
        }
      },
      
      // Utility functions
      getSelectedNodes: () => {
        const { nodes, universalNodes, selection } = get();
        return Array.from(selection).map(id => {
          const legacyNode = nodes.get(id);
          if (legacyNode) return legacyNode;
          
          // For universal nodes, create a compatible structure
          const universalNode = universalNodes[id];
          if (universalNode) {
            const position = universalNode.values.position || { x: 0, y: 0 };
            return {
              id,
              position,
              size: { width: 200, height: 120 }, // default size
              classType: universalNode.definitionId,
              title: universalNode.definitionId,
              inputs: [],
              outputs: [],
              selected: true,
              dragging: false,
              color: '#3b82f6',
              bgcolor: '#ffffff',
              data: universalNode.values
            } as CanvasNode;
          }
          
          return null;
        }).filter(Boolean) as CanvasNode[];
      },
      
      getNodeConnections: (nodeId: string) => {
        const { connections } = get();
        return Array.from(connections.values()).filter(
          conn => conn.sourceNodeId === nodeId || conn.targetNodeId === nodeId
        );
      },
      
      findConnectionsToSocket: (nodeId: string, socketId: string) => {
        const { connections } = get();
        return Array.from(connections.values()).filter(
          conn => 
            (conn.sourceNodeId === nodeId && conn.sourceSocketId === socketId) ||
            (conn.targetNodeId === nodeId && conn.targetSocketId === socketId)
        );
      },
      
      // Enhanced persistence operations
      initializePersistence: async () => {
        const { workflowPersistenceService } = await import('../services/WorkflowPersistenceService');
        await workflowPersistenceService.initialize();
        
        // Try to restore last session
        const session = workflowPersistenceService.getCurrentSession();
        if (session && session.workflow) {
          get().loadUniversalWorkflow(session.workflow);
        }
      },

      hasUnsavedChanges: () => {
        return workflowPersistenceService.hasUnsavedChanges();
      },

      saveCurrentWorkflow: async (name?: string) => {
        const { workflowPersistenceService } = await import('../services/WorkflowPersistenceService');
        const currentWorkflow = get().exportUniversalWorkflow();
        
        // Update persistence service with current workflow
        await workflowPersistenceService.updateWorkflow(currentWorkflow);
        
        // Perform manual save
        return await workflowPersistenceService.saveWorkflow(name);
      },

      getRecentWorkflows: async () => {
        const { workflowPersistenceService } = await import('../services/WorkflowPersistenceService');
        return await workflowPersistenceService.getRecentWorkflows();
      },

      enableAutoSave: (enabled: boolean) => {
        import('../services/WorkflowPersistenceService').then(({ workflowPersistenceService }) => {
          workflowPersistenceService.setAutoSaveEnabled(enabled);
        });
      },

      isValidConnection: (sourceNodeId: string, sourceSocketId: string, targetNodeId: string, targetSocketId: string) => {
        console.log('Validating connection:', {
          sourceNodeId,
          sourceSocketId,
          targetNodeId,
          targetSocketId
        });
        
        // Cannot connect to self
        if (sourceNodeId === targetNodeId) {
          return false;
        }
        
        // Get nodes to check socket types
        const { nodes, universalNodes, nodeDefinitions } = get();
        const sourceLegacyNode = nodes.get(sourceNodeId);
        const targetLegacyNode = nodes.get(targetNodeId);
        const sourceUniversalNode = universalNodes[sourceNodeId];
        const targetUniversalNode = universalNodes[targetNodeId];
        
        console.log('Available nodes:', {
          sourceLegacyNode: !!sourceLegacyNode,
          targetLegacyNode: !!targetLegacyNode,
          sourceUniversalNode: !!sourceUniversalNode,
          targetUniversalNode: !!targetUniversalNode
        });
        
        // At least one node must exist
        if (!sourceLegacyNode && !sourceUniversalNode) {
          return false;
        }
        if (!targetLegacyNode && !targetUniversalNode) {
          return false;
        }
        
        // Check if connection already exists to target socket (inputs can only have one connection)
        const existingConnection = get().findConnectionsToSocket(targetNodeId, targetSocketId);
        if (existingConnection.length > 0) {
          return false;
        }
        
        // Advanced type checking
        let sourceSocketType: string | undefined;
        let targetSocketType: string | undefined;
        
        // Get source socket type
        if (sourceLegacyNode) {
          // For legacy nodes, extract socket type from outputs
          const outputMatch = sourceSocketId.match(/output_(\d+)/);
          if (outputMatch) {
            const outputIndex = parseInt(outputMatch[1], 10);
            if (sourceLegacyNode.outputs && sourceLegacyNode.outputs[outputIndex]) {
              sourceSocketType = sourceLegacyNode.outputs[outputIndex].type;
            }
          }
        } else if (sourceUniversalNode) {
          // For universal nodes, get from definition
          const definition = nodeDefinitions[sourceUniversalNode.definitionId];
          if (definition) {
            const outputSocket = definition.outputs.find(o => o.id === sourceSocketId);
            sourceSocketType = outputSocket?.type;
          }
        }
        
        // Get target socket type
        if (targetLegacyNode) {
          // For legacy nodes, extract socket type from inputs
          const inputMatch = targetSocketId.match(/input_(\w+)/);
          if (inputMatch) {
            const inputName = inputMatch[1];
            if (targetLegacyNode.inputs) {
              const inputSocket = targetLegacyNode.inputs.find(i => i.name === inputName);
              targetSocketType = inputSocket?.type;
            }
          }
        } else if (targetUniversalNode) {
          // For universal nodes, get from definition
          const definition = nodeDefinitions[targetUniversalNode.definitionId];
          if (definition) {
            const inputSocket = definition.inputs.find(i => i.id === targetSocketId);
            targetSocketType = inputSocket?.type;
          }
        }
        
        
        // Type compatibility check
        if (sourceSocketType && targetSocketType) {
          const isCompatible = get().areTypesCompatible(sourceSocketType, targetSocketType);
          if (!isCompatible) {
            return false;
          }
        }
        
        // Check for circular dependencies
        if (get().wouldCreateCycle(sourceNodeId, targetNodeId)) {
          return false;
        }
        
        return true;
      },

      // Enhanced Type compatibility checking
      areTypesCompatible: (sourceType: string, targetType: string) => {
        try {
          // Use Enhanced Type System for validation
          const validationService = TypeValidationService.getInstance();
          const result = validationService.validateConnection(
            sourceType as EnhancedNodeType, 
            targetType as EnhancedNodeType
          );
          
          console.log('Enhanced type validation:', {
            sourceType,
            targetType,
            isValid: result.isValid,
            message: result.message
          });
          
          return result.isValid;
        } catch (error) {
          console.warn('Enhanced type validation failed, falling back to legacy system:', error);
          
          // Exact type match as fallback
          if (sourceType === targetType) {
            return true;
          }
          
          // Enhanced types fallback mapping
          const enhancedCompatibilityMap: Record<string, string[]> = {
            // Legacy to Enhanced mappings
            'string': ['text', 'STRING', 'DATA_TEXT'],
            'text': ['string', 'STRING', 'DATA_TEXT'], 
            'DATA_TEXT': ['string', 'text', 'STRING', 'DATA_ANY'],
            
            'number': ['int', 'float', 'INTEGER', 'FLOAT', 'DATA_NUMBER'],
            'DATA_NUMBER': ['number', 'int', 'float', 'INTEGER', 'FLOAT', 'DATA_ANY'],
            
            'boolean': ['BOOLEAN', 'bool', 'DATA_BOOLEAN'],
            'DATA_BOOLEAN': ['boolean', 'BOOLEAN', 'bool', 'DATA_ANY'],
            
            // Enhanced DATA types
            'DATA_ANY': ['DATA_TEXT', 'DATA_NUMBER', 'DATA_BOOLEAN', 'DATA_IMAGE', 'DATA_OBJECT', 'DATA_ARRAY', 'DATA_FILE', 'DATA_URL', 'DATA_JSON', 'string', 'text', 'number', 'boolean'],
            
            // SIGNAL types
            'exec': ['EXEC', 'trigger', 'flow', 'SIGNAL_TRIGGER'],
            'SIGNAL_TRIGGER': ['exec', 'EXEC', 'trigger', 'flow', 'SIGNAL_ANY'],
            'SIGNAL_ANY': ['SIGNAL_TRIGGER', 'SIGNAL_EVENT', 'SIGNAL_FLOW'],
            
            // Legacy compatibility
            'IMAGE': ['image', 'tensor', 'DATA_IMAGE'],
            'DATA_IMAGE': ['IMAGE', 'image', 'tensor', 'DATA_ANY'],
            
            // Any types
            'any': ['*'],
            '*': ['any'],
          };
  
          const compatibleTypes = enhancedCompatibilityMap[sourceType] || [];
          return compatibleTypes.includes(targetType);
        }
      },

      // Cycle detection
      wouldCreateCycle: (sourceNodeId: string, targetNodeId: string) => {
        const { connections } = get();
        
        // Build adjacency list from existing connections
        const graph: Record<string, string[]> = {};
        
        for (const connection of connections.values()) {
          if (!graph[connection.sourceNodeId]) {
            graph[connection.sourceNodeId] = [];
          }
          graph[connection.sourceNodeId].push(connection.targetNodeId);
        }
        
        // Add the proposed connection to check for cycle
        if (!graph[sourceNodeId]) {
          graph[sourceNodeId] = [];
        }
        graph[sourceNodeId].push(targetNodeId);
        
        // DFS to detect cycle starting from targetNodeId
        const visited = new Set<string>();
        const recursionStack = new Set<string>();
        
        const hasCycle = (nodeId: string): boolean => {
          if (recursionStack.has(nodeId)) {
            return true; // Back edge found - cycle detected
          }
          
          if (visited.has(nodeId)) {
            return false; // Already processed
          }
          
          visited.add(nodeId);
          recursionStack.add(nodeId);
          
          const neighbors = graph[nodeId] || [];
          for (const neighbor of neighbors) {
            if (hasCycle(neighbor)) {
              return true;
            }
          }
          
          recursionStack.delete(nodeId);
          return false;
        };
        
        return hasCycle(targetNodeId);
      }
    })),
    {
      name: 'canvas-store'
    }
  )
);